import { useState } from "react";
import LegalShell, { Section } from "@/components/LegalShell";
import { api, HttpError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Phone, MapPin, CheckCircle2, Send } from "lucide-react";

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sent | error
  const [errMsg, setErrMsg] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setErrMsg("");
    if (!name.trim() || !email.trim() || !message.trim()) {
      setErrMsg("Name, email and message are all required.");
      return;
    }
    setBusy(true);
    try {
      await api.submitContact({ name: name.trim(), email: email.trim(), message: message.trim() });
      setStatus("sent");
      setName("");
      setEmail("");
      setMessage("");
    } catch (e) {
      setStatus("error");
      if (e instanceof HttpError) {
        setErrMsg(e.body?.detail?.[0]?.msg || e.body?.detail || "Could not send.");
      } else {
        setErrMsg("Network error. Please email us directly at praveen@thefoundermode.in.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <LegalShell testid="contact-page" eyebrow="contact" title="Contact Us">
      <Section heading="Reach us">
        <div
          data-testid="contact-info-block"
          className="rounded-xl border border-amber-500/20 bg-zinc-950/40 p-5 space-y-3 font-mono text-[13px] text-zinc-300"
        >
          <div className="flex items-start gap-3">
            <MapPin className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-zinc-100">Super Founder Hub</div>
              <div>Kanapura Main Road</div>
              <div>Bangalore, Karnataka, India</div>
              {/* PIN code provided by user */}
              <div>PIN: 560062</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="h-4 w-4 text-amber-300 shrink-0" />
            <a
              href="mailto:praveen@thefoundermode.in"
              className="text-amber-300 hover:text-amber-200 break-all"
              data-testid="contact-email-link"
            >
              praveen@thefoundermode.in
            </a>
          </div>
          <div className="flex items-center gap-3">
            <Phone className="h-4 w-4 text-amber-300 shrink-0" />
            <span data-testid="contact-phone">+91 94483 10439</span>
          </div>
          <div className="text-zinc-500 pt-2 border-t border-zinc-900">
            Support hours: Monday–Friday, 10 AM – 6 PM IST
          </div>
        </div>
      </Section>

      <Section heading="Send us a message">
        {status === "sent" ? (
          <div
            data-testid="contact-success-state"
            className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-5 flex gap-3 items-start"
          >
            <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-emerald-200 font-semibold">
                Thank you — message received.
              </p>
              <p className="text-emerald-300/80 text-sm mt-1">
                We&apos;ll reply within 1 business day.
              </p>
            </div>
          </div>
        ) : (
          <form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
                Name
              </label>
              <Input
                data-testid="contact-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                disabled={busy}
                className="bg-zinc-900/60 border-amber-500/20 text-zinc-100 placeholder:text-zinc-600 h-11"
              />
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
                Email
              </label>
              <Input
                data-testid="contact-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={busy}
                className="bg-zinc-900/60 border-amber-500/20 text-zinc-100 placeholder:text-zinc-600 h-11"
              />
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
                Message
              </label>
              <textarea
                data-testid="contact-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="How can we help?"
                disabled={busy}
                rows={6}
                className="w-full rounded-md border border-amber-500/20 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
              />
            </div>
            {errMsg && (
              <p data-testid="contact-error" className="text-xs text-red-400">
                {errMsg}
              </p>
            )}
            <Button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={busy}
              className="h-11 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
            >
              <Send className="h-4 w-4" />
              {busy ? "Sending…" : "Send message"}
            </Button>
          </form>
        )}
      </Section>
    </LegalShell>
  );
}
